/**
 * Peças comuns das telas de cadastro: busca, filtro de status, paginação,
 * formulário simples com validação pelo MESMO schema da API e salvamento
 * idempotente (criação) ou com trava de versão (alteração).
 */
import type { CadastroOptions, Page } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiFetch, newIdempotencyKey } from "@/lib/api";
import { notifySuccess } from "@/lib/notice";

export type StatusFilter = "active" | "inactive" | "all";

/** Opções dos campos de seleção (unidades, formas, tabelas, vendedores, usuários). */
export function useCadastroOptions() {
  return useQuery({
    queryKey: ["cadastros", "opcoes"],
    queryFn: () => apiFetch<CadastroOptions>("/cadastros/opcoes"),
    staleTime: 30_000,
  });
}

/** Lista paginada com busca e filtro de status. */
export function useCadastroList<T>(
  resource: string,
  params: { q: string; status: StatusFilter; offset: number; extra?: Record<string, string> },
) {
  const search = new URLSearchParams({
    status: params.status,
    limit: "50",
    offset: String(params.offset),
    ...(params.q.trim() ? { q: params.q.trim() } : {}),
    ...params.extra,
  });
  return useQuery({
    queryKey: ["cadastros", resource, search.toString()],
    queryFn: () => apiFetch<Page<T>>(`/cadastros/${resource}?${search.toString()}`),
    placeholderData: (prev) => prev,
  });
}

/** Erros por campo a partir do resultado do schema (Zod). */
export type FieldErrors = Record<string, string>;
export function issuesToErrors(
  issues: readonly { path: readonly PropertyKey[]; message: string }[],
): FieldErrors {
  const out: FieldErrors = {};
  for (const i of issues) {
    const key = i.path.map(String).join(".");
    if (!(key in out)) out[key] = i.message;
  }
  return out;
}

/** Estado de formulário simples. */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const set =
    <K extends keyof T>(key: K) =>
    (value: T[K]) =>
      setValues((v) => ({ ...v, [key]: value }));
  /** Liga um <Input> de texto a um campo. */
  const text = (key: keyof T & string) => ({
    value: (values[key] ?? "") as string,
    onChange: (e: { target: { value: string } }) =>
      setValues((v) => ({ ...v, [key]: e.target.value })),
  });
  /** Liga um checkbox a um campo booleano. */
  const check = (key: keyof T & string) => ({
    checked: Boolean(values[key]),
    onChange: (e: { target: { checked: boolean } }) =>
      setValues((v) => ({ ...v, [key]: e.target.checked })),
  });
  return { values, setValues, errors, setErrors, set, text, check };
}

/**
 * Salvar cadastro:
 *  - criação: POST com UMA chave de idempotência por formulário aberto
 *    (clique duplo ou nova tentativa não duplicam);
 *  - alteração: PUT com a versão lida (se outra pessoa salvou antes, a API recusa).
 */
export function useSave<TBody, TResult>(opts: {
  resource: string;
  id: string | null;
  invalidate: string[];
  onSaved?: (r: TResult) => void;
  /** aviso depois que a API confirmou (ex.: "Cliente criado com sucesso. Código: 000012.") */
  successMessage?: (r: TResult, created: boolean) => string;
}) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState(newIdempotencyKey);
  return useMutation({
    mutationFn: (body: TBody) =>
      opts.id
        ? apiFetch<TResult>(`/cadastros/${opts.resource}/${opts.id}`, { method: "PUT", body })
        : apiFetch<TResult>(`/cadastros/${opts.resource}`, {
            method: "POST",
            body,
            idempotencyKey: key,
          }),
    onSuccess: async (r) => {
      setKey(newIdempotencyKey());
      for (const k of opts.invalidate)
        await queryClient.invalidateQueries({ queryKey: ["cadastros", k] });
      if (opts.successMessage) notifySuccess(opts.successMessage(r, opts.id === null));
      opts.onSaved?.(r);
    },
  });
}

/** Texto de campo vindo da API (null → ""). */
export const str = (v: string | number | null | undefined) =>
  v === null || v === undefined ? "" : String(v);

/** Estado de busca/filtro/página de uma listagem (voltar à 1ª página ao filtrar). */
export function useListState(initialStatus: StatusFilter = "active") {
  const [q, setQState] = useState("");
  const [status, setStatusState] = useState<StatusFilter>(initialStatus);
  const [offset, setOffset] = useState(0);
  return {
    q,
    status,
    offset,
    setOffset,
    setQ: (v: string) => {
      setQState(v);
      setOffset(0);
    },
    setStatus: (v: StatusFilter) => {
      setStatusState(v);
      setOffset(0);
    },
  };
}
